import { action, observable, toJS } from 'mobx';
import stores from 'cd-eft/models/Stores';
import { displaySurveyPointsClientSide } from '../Maps/SETMapFunctions/loadLayers';
import { saveXYZSurvey, cancelEditXYZSurvey } from '../SurveyListDataGrid/SurveyListDataGridVM';
import { DesignContainerVM } from '../../Design/DesignContainer/DesignContainerVM';
import { AppConstants } from 'cd-eft/appConstants';
import { SpatialRefRequestDto } from 'cd-eft/_codegen_/openapi-generator/eft-prjadmin';
import { removeSingleLayerFromMap, getSurveys } from '../SurveySummary/SurveySummaryVM';
import { GroundPointEditAdapter } from 'cd-eft/components/Design/BreaklineTools/model/GroundPointEditAdapter';
import { GroundPoint } from 'cd-eft/components/Design/BreaklineTools/model/GroundPoint';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { appConfig } from 'cd-eft/config/AppConfig';
import { noSpecialChars } from '../../../utils/noSpecialChars';
import { getProjectionByLocation, getStateNameByStateCode, getSpatialRefByProject } from 'cd-eft/components/Projects/ProjectsLibrary/ProjectsLibraryVM';
import {SurfaceDefController, SetSurveyController} from 'cd-eft/services/eftSetApi';
import { ProjectionMetadataCustomizedController, ProjectionMetadataController, SpatialRefController} from 'cd-eft/services/eftPrjAdminApi';
import { SpatialReference, Point, } from 'cd-eft/utils/esriTypes';
import { project } from '@arcgis/core/geometry/projection';
import { SurfaceDef } from 'cd-eft/components/Design/BreaklineTools/model/SurfaceDef';
import { INotification } from 'cd-eft/models/Stores/DesignStore';

const setStore = stores.AppStore.surveyEngineeringStore;
const libraryStore = stores.AppStore.projectsLibraryStore;
const designStore = stores.AppStore.designStore;
const gisModel = stores.AppStore.surveyEngineeringStore.gisModel;


export const headerSelection = {
  stringOptions: [
    { label: 'Name', value: 'pointName' },
    { label: 'Description', value: 'featureDescription' },
  ],
  numOptions: [
    { label: 'X(Easting)', value: 'x' },
    { label: 'Y(Northing)', value: 'y' },
    { label: 'Z(Elevation)', value: 'z' },
  ],
  allOptions: [
    { label: 'X(Easting)', value: 'x' },
    { label: 'Y(Northing)', value: 'y' },
    { label: 'Z(Elevation)', value: 'z' },
    { label: 'Name', value: 'pointName' },
    { label: 'Description', value: 'featureDescription' },
  ],
};

export class SurveyPointsDataGridVM {
  @observable props;
  @observable previewProjectedSurvey: boolean = false;
  private previewSpatialReference?: SpatialReference = undefined;
  private spatialReferenceForLatLong?: SpatialReference = undefined;
  @observable isAccordionExpanded: boolean = true;
  @observable uniqueDescriptions: string[] = [];

  constructor(props: any) {
    this.props = props;
    if (!setStore.isImportingNewSurvey) {
      setStore.triggerSurfaceDesignerVm?.drawSurveyPoints();
      setStore.triggerBreaklineToolboxVM?.drawGroundPoints();
    }
  }

  @action.bound setDescriptionFilter(filter) {
    this.props.stopWatchHandler();
    setStore.pointDescriptionFilter = filter;
    for (const point of setStore.xyzPointsTable) {
      point.filtered = (setStore.pointDescriptionFilter === "All" || point.description === setStore.pointDescriptionFilter) ? true : false;
    }
    if (!setStore.isImportingNewSurvey) {
      setStore.triggerSurfaceDesignerVm?.drawSurveyPoints();
      setStore.triggerBreaklineToolboxVM?.drawGroundPoints();
    }
    this.props.startWatchHandler();
  }

  @action.bound setUniqueDescriptions() {
    if (setStore.xyzPointsTable && setStore.xyzPointsTable.length > 0) {
      const tempArray = [...new Set(setStore.xyzPointsTable?.map((item: any) => item.description))] as string[];
      this.uniqueDescriptions = tempArray.sort((a,b) => a.localeCompare(b, undefined, {numeric: true, sensitivity: 'base'}));
    }
  }

  @action.bound async previewSurveySwitchHandler() {
    this.previewProjectedSurvey = !this.previewProjectedSurvey;

    this.duplicatesList.length = 0;

    if (this.previewProjectedSurvey) {
      if (!this.newSurvey.coordinateSystemType) {
        this.previewProjectedSurvey = false;
        MessageCenter.addWarningFromCode('Projection_01');
      } else {
        this.previewProjectedSurveyPoints()
      }
    } else {
      this.removeProjectedSurveypoints()
    }
  }
  @action toggleAccordion() {
    this.isAccordionExpanded = !this.isAccordionExpanded;
  }

  private async previewProjectedSurveyPoints() {
    try {
      if (!libraryStore.pojectionByLocationData?.esriWkid) {
        const projectionName = this.getFormattedProjectionName();
        const response = await ProjectionMetadataCustomizedController.getProjectionMetadataCustomizedsByProjectionNameUsingGET((projectionName === '_Ft_Intl') ? libraryStore.projectSpatialRefData.projectionName : projectionName);
        if (response.data) {
          this.previewSpatialReference = new SpatialReference({ wkt: response.data[0].wktJson });
        }
      } else if (libraryStore.pojectionByLocationData?.esriWkid) {
        this.previewSpatialReference = new SpatialReference({ wkid: libraryStore.pojectionByLocationData.esriWkid });
      }
      await this.setXYZPoints();
      this.onDisplayMap(true, this.previewSpatialReference);
    } catch (error) {
      MessageCenter.addError((error as Error).message);
      return;
    } finally {
      setStore.xyzPointsTable = null;
      setStore.xyzPointsTable = []
    }
  }

  private removeProjectedSurveypoints() {
    this.previewProjectedSurvey = false;
    this.onDisplayMap(false);
  }

  private readonly _currentWkid = appConfig.tempHardCodes.wkid;
  @observable newSurvey = {
    surveyName: '',
    surfaceName: designStore.currentSurface.surfaceDefTitle,
    coordinateSystemType: libraryStore.projectSpatialRefData.coordinateType ? libraryStore.projectSpatialRefData.coordinateType : '',
    zone: libraryStore.projectSpatialRefData.zone ? libraryStore.projectSpatialRefData.zone : ''
  };

  @observable newSurface = {
    surfaceName: designStore.currentSurface.surfaceDefTitle,
  };

  @observable formValidation = {
    formErrors: {
      surveyName: '',
      surfaceName: '',
      coordinateSystemType: '',
      zone: ''
    },
    surveyNameValid: false,
    surfaceNameValid: false,
    coordinateSystemTypeValid: false,
    zoneValid: false,
    formValid: false,
  };

  // Setting checkboxes
  @observable checkboxes: Array<number> = [];

  @observable spatialRefPayload: SpatialRefRequestDto | null = null;
  @observable notification: INotification = {
    message: '',
    header: '',
    type: '',
    hidden: true,
    timestamp: undefined,
  };
  @observable projectionIsNotMatch: boolean = false;

  @action.bound setSurveyName(event: any) {
    const { name, value } = event.target;
    this.newSurvey[name] = value;
    this.validateField(name, value);
    this.newSurvey.surveyName = value;
    setStore.currentSurvey.surveyName = this.newSurvey.surveyName;
  }

  @action.bound setSurfaceName(event: any) {
    const { name, value } = event.target;
    this.newSurface[name] = value;
    this.validateField(name, value);
    this.newSurface.surfaceName = value;
    designStore.currentSurface.surfaceDefTitle = this.newSurface.surfaceName;
  }
  @action.bound async updateSurfaceName() {
    const postJSON = {
      projectUuid: designStore.projectsStore.currentProjectId,
      status: 'update',
      surfaceName: designStore.currentSurface.surfaceDefTitle,
    };

    try {
      await SurfaceDefController.updateSurfaceNameByProjectUuidUsingPOST(postJSON);
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }
  @action.bound handleCoordinateSystemChange(event) {
    event.preventDefault();
    this.removeProjectedSurveypoints();
    const { name, value } = event.target;
    this.newSurvey[name] = value;
    this.validateField(name, value);
  }
  @action.bound setContourInterval= async(value:number)  =>{
    designStore.contourInterval = value;
  }

  @action.bound async convertXYToLatLong(spatialReference: any): Promise<{ latitude: number, longitude: number } | null> {
    if (!spatialReference) {
      return null;
    }
    const xIndex = setStore.surveyFileHeader.indexOf('x');
    const yIndex = setStore.surveyFileHeader.indexOf('y');

    const dataRowCount = setStore.tempXYZPointsTable.length;
    const examinationRow = dataRowCount > 1 ? 1 : 0;

    const secondEntry = setStore.tempXYZPointsTable[examinationRow];
    const xValue = secondEntry[xIndex];
    const yValue = secondEntry[yIndex];

    const latLongPointXY = new Point({
      x: xValue,
      y: yValue,
      spatialReference: spatialReference
    });

    const wgs84 = new SpatialReference({
      wkid: 4326
    });

    const latLong = project(latLongPointXY, wgs84) as Point;
    if (!latLong || !latLong.latitude || !latLong.longitude) {
      return null;
    }
    return {
      latitude: latLong.latitude,
      longitude: latLong.longitude
    };
  }
  @action.bound getFormattedProjectionName(zoneName?: string): string {
    let projectionName = libraryStore.pojectionByLocationData.length === 1
      ? libraryStore.pojectionByLocationData[0].projectionName
      : libraryStore.pojectionByLocationData.find((data) => data.zoneName === (zoneName || this.newSurvey.zone))?.projectionName || '';
  
    // Ensure it has '_Ft_Intl' suffix
    const shouldAddFtIntl = !projectionName.includes('_Ft_Intl');
    projectionName = shouldAddFtIntl ? `${projectionName}_Ft_Intl` : projectionName;
  
    // Double-check for consistency (redundant check in original code, kept for compatibility)
    projectionName = projectionName.includes("Ft_Intl") ? projectionName : `${projectionName}_Ft_Intl`;  
    return projectionName;
  }

  @action.bound async validateSurveyDataBounds(): Promise<boolean> {
    const xIndex = setStore.surveyFileHeader.indexOf('x');
    const yIndex = setStore.surveyFileHeader.indexOf('y');
  
    const dataRowCount = setStore.tempXYZPointsTable.length;
    const examinationRow = dataRowCount > 1 ? 1 : 0;
  
    const secondEntry = setStore.tempXYZPointsTable[examinationRow];
    const xValue = secondEntry[xIndex];
    const yValue = secondEntry[yIndex];
  
    const projectionName = this.getFormattedProjectionName();
    const payload = {
      state: designStore.projectsStore.currentProject.state, 
      county: designStore.projectsStore.currentProject.county,
      definedUTM: (projectionName === '_Ft_Intl') 
        ? libraryStore.projectSpatialRefData.projectionName.replace(/_/g, ' ') 
        : projectionName.replace(/_/g, ' '),
      projectUuid: designStore.projectsStore.currentProjectId,
      x: xValue,
      y: yValue
    }
  
    const response = await SetSurveyController.validateSurveyDataWithinDefinedUTMandCountyUsingPOST(payload);
    
    if (response.data && response.data.errorResponse && response.data.errorResponse.errorCode) {
      await MessageCenter.addErrorFromCode(response.data.errorResponse.errorCode);
      return false;
    }
    return true;
  }


  @action.bound async getProjectionfromUploadSurvey(state, county) {
    const stateName = await getStateNameByStateCode(state);
    if (
      (!setStore.surveySummaryTable || setStore.surveySummaryTable.length <= 0)
    ) {
      try {
        if (!libraryStore.pojectionByLocationData?.esriWkid) {
          const projectionName = this.getFormattedProjectionName();
          const response = await ProjectionMetadataCustomizedController.getProjectionMetadataCustomizedsByProjectionNameUsingGET((projectionName == '_Ft_Intl') ? libraryStore.projectSpatialRefData.projectionName : projectionName);
          if (response.data) {
            this.spatialReferenceForLatLong = new SpatialReference({ wkt: response.data[0].wktJson });
          }
        } else if (libraryStore.pojectionByLocationData?.esriWkid) {
          this.spatialReferenceForLatLong = new SpatialReference({ wkid: libraryStore.pojectionByLocationData.esriWkid });
        }
      } catch (error) {
        MessageCenter.addError((error as Error).message);
        return;
      }
    }
    let spatialReference;
    if (this.newSurvey.zone || (this.newSurvey.coordinateSystemType !== libraryStore.projectSpatialRefData.coordinateType)) {
      if (typeof this.spatialReferenceForLatLong !== 'undefined') {
        spatialReference = this.spatialReferenceForLatLong;
      } else {
        if (typeof this.spatialReferenceForLatLong === 'undefined' || (!setStore.surveySummaryTable || setStore.surveySummaryTable.length <= 0)) {
          spatialReference = libraryStore.projectSpatialReference;
        } else {
          spatialReference = this.spatialReferenceForLatLong;
        }
      }
    }

   const latLongResult = await this.convertXYToLatLong(spatialReference);
    let latitude, longitude;
    if (latLongResult) {
      latitude = latLongResult.latitude;
      longitude = latLongResult.longitude;
    }
    const callAPI = async (coordinateSystemType: string) => {
      const isStatePlane = coordinateSystemType?.includes('StatePlane');
      const payload = {
        coordinateType: coordinateSystemType,
        ...(isStatePlane
          ? {
            state: stateName,
            centroidX: longitude,
            centroidY: latitude,
          }
          : {
            xmax: longitude,
            xmin: longitude,
            ymax: latitude,
            ymin: latitude,
          }),
      };

      const isDataComplete = Object.values(payload).every(value => value !== undefined && value !== '');
      if (isDataComplete && state && county) {
        try {
          const response = await ProjectionMetadataController.getProjectionByLocationUsingPOST(payload);
          return {
            zoneName: response.data[0].zoneName,
            projectionName: response.data[0].projectionName,
            coordinateSystemType: coordinateSystemType
          };
        } catch (error) {
          MessageCenter.addError((error as Error).message);
        }
      }
    };


    const result = await callAPI(this.newSurvey.coordinateSystemType);
    const zoneNameNumber = result?.zoneName ? parseInt(result.zoneName, 10) : null;
    const zoneNumberFromLibrary = this.newSurvey.zone ?
      parseInt(this.newSurvey.zone, 10) :
      parseInt(libraryStore.projectSpatialRefData.zone, 10);

    // For the first survey
    if (!setStore.surveySummaryTable || setStore.surveySummaryTable.length <= 0) {
      if (zoneNameNumber !== null && zoneNumberFromLibrary !== null && Math.abs(zoneNameNumber - zoneNumberFromLibrary) === 1) {
        const messageg = await MessageCenter.addWarningFromCode('Projection_5');
      } else if (!result || zoneNameNumber === null || zoneNumberFromLibrary === null || Math.abs(zoneNameNumber - zoneNumberFromLibrary) > 1) {
        this.projectionIsNotMatch = true;
        await MessageCenter.addErrorFromCode('Projection_4');
      }
    }
    // For the second survey
    else {
      if (zoneNameNumber !== null && zoneNumberFromLibrary !== null && Math.abs(zoneNameNumber - zoneNumberFromLibrary) === 1) {
        await MessageCenter.addWarningFromCode('Projection_5');
      } else if (!result || zoneNameNumber === null || zoneNumberFromLibrary === null || Math.abs(zoneNameNumber - zoneNumberFromLibrary) > 1) {
        this.projectionIsNotMatch = true;
        await MessageCenter.addErrorFromCode('Project_9');
      }
    }
  }
  @action.bound handleZoneInputChange(event) {
    event.preventDefault();
    this.removeProjectedSurveypoints();
    const { name, value } = event.target;
    this.newSurvey[name] = value
    this.validateField(name, value);
  }

  @action.bound async projectionByLocation() {
    libraryStore.pojectionByLocationData = []
    if (this.newSurvey.coordinateSystemType !== '') {
      const response = await getProjectionByLocation(this.newSurvey.coordinateSystemType, designStore.projectsStore.currentProject.state, designStore.projectsStore.currentProject.county)
      if (!libraryStore.projectSpatialRefData.zone && libraryStore.pojectionByLocationData.length > 1) {
        this.newSurvey.zone = ''
      }
      else {
        if (response && response.length === 1) {
           this.newSurvey.zone = response[0].zoneName;
         } 
      }
    }
  }

  @action.bound async validateField(fieldName, value) {
    // connects form field to validation table
    const keyValid = fieldName + 'Valid';
    let fieldValid = this.formValidation[keyValid];
    const fieldValidationErrors = this.formValidation.formErrors;
    let errorCode: string = '';
    /**
     * Survey_07: Survey Name is required and only numbers and letters are allowed.
     * Survey_24: Surface Name is required and only numbers and letters are allowed.
     */
    switch (fieldName) {
      case 'surveyName':
        fieldValid = value !== '' && noSpecialChars(value);
        errorCode = fieldValid ? '' : 'Survey_07';
        break;
      case 'surfaceName':
        fieldValid = value !== '' && noSpecialChars(value);
        errorCode = fieldValid ? '' : 'Survey_24';
        break;
      case 'coordinateSystemType':
        fieldValid = !(value === '' || value === 0);
        errorCode = fieldValid ? '' : 'Common_08';      // value is required.
        // If the coordinate system type is valid, clear the zone error
        if (fieldValid) {
          this.formValidation['zoneValid'] = true;
          await this.getErrorMessage('', this.formValidation.formErrors, 'zone');
        }
        break;
      case 'zone':
        // Skip validation if coordinate system type is valid
        if (this.formValidation['coordinateSystemTypeValid']) {
          break;
        }
        fieldValid = !(value === '' || value === 0);
        errorCode = fieldValid ? '' : 'Common_08';      // value is required.
        break;
      default:
        break;
    }
    this.formValidation.formErrors = fieldValidationErrors;
    await this.getErrorMessage(errorCode, this.formValidation.formErrors, fieldName);
    this.formValidation[keyValid] = fieldValid;
    this.validateForm();
  }

  @action.bound async getErrorMessage(code: string, container, fieldName: string) {
    if (code === '') {
      container[fieldName] = '';
    } else {
      const message = await MessageCenter.addErrorLabelFromCode(code);
      container[fieldName] = message;
      let field;
      if (fieldName !== 'surveyName' && fieldName !== 'surfaceName') {
        field = fieldName
          .split(/(?=[A-Z])/)
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      await MessageCenter.addNotificationFromCode(code, undefined, field);
    }
  }

  @action.bound validateForm() {
    let valid = true;
    for (const key in this.formValidation.formErrors) {
      if (!this.formValidation[key + 'Valid']) {
        valid = false;
      }
    }
    this.formValidation.formValid = valid;
  }

  @action.bound errorClass(field) {
    const errorMessage = this.formValidation.formErrors[field];
    return errorMessage.length === 0 ? '' : ' usa-input--error';
  }

  @action.bound clearNotification(): void {
    this.notification = { message: '', header: '', type: '', hidden: true, timestamp: undefined };
  }

  @action.bound toggleCheckbox(e) {
    const i = Number(e.target.value);
    if (e.target.checked) {
      this.checkboxes = [...this.checkboxes, i];
    } else {
      const items = this.checkboxes.filter((p) => (p !== i));
      this.checkboxes = items;
    }
  }

  @observable error = false;
  @observable errorMsg = '';
  @observable shouldDisplayOnMap = false;

  @action.bound getColumnHeader(event, i) {
    this.removeProjectedSurveypoints();
    this.clearNotification();
    setStore.surveyFileHeader[i] = event.target.value;
    this.error = false;
    this.errorMsg = '';
  }

  // Display ground points on map
  @action.bound onDisplayMap(shouldDisplayOnMap: boolean, previewSR?: SpatialReference | undefined) {
    this.shouldDisplayOnMap = shouldDisplayOnMap;
    const survey1 = setStore.xyzPointsTable;
    const layerId = AppConstants.newSurveyLayerId;
    const renderer = {
      type: 'simple',
      symbol: {
        type: 'simple-marker',
        color: 'green',
        size: '5',
        outline: null,
        style: 'triangle',
      },
    };
    displaySurveyPointsClientSide(
      gisModel.view,
      survey1,
      layerId,
      setStore.currentSurvey.surveyName,
      this.shouldDisplayOnMap,
      renderer,
      this._currentWkid,
      true,
      previewSR
    );
    setStore.pointsOnMap = shouldDisplayOnMap;
  }

  //visible and tinable checkbox onChange functionality
  @action.bound handleTinableCheckboxChange(e: any) {
    this.handleCheckboxChange(e);
    setStore.saveTINSurface = true;
  }

  handleRowMouseEnter(point: GroundPointEditAdapter) {
    if (!point.eft_type) {
      const groundPoint = new GroundPoint();
      groundPoint.id = point.id;
      groundPoint.name = point.pointName;
      groundPoint.x = point.x;
      groundPoint.y = point.y;
      groundPoint.z = point.z;
      groundPoint.wkid = appConfig.tempHardCodes.wkid;
      groundPoint.editing_status = point.editing_status;
      groundPoint.visible = point.visible;
      groundPoint.description = point.featureDescription;
      EngineeringMapService.instance?.trackPoint(groundPoint);
    }
    else{
      EngineeringMapService.instance?.trackPoint(point);
    }
  }

  handleRowMouseLeave() {
    EngineeringMapService.instance?.unTrackLast();
  }

  @action.bound async handleCheckboxChange(event: any) {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    const index = target.getAttribute('data-i');
    if (name === 'tinable') {
      setStore.xyzPointsTable[index].tinable = value ? 1 : 0;
      if (value) {
        setStore.xyzPointsTable[index].visible = 1;
      }
      setStore.initialXyzPointsTable[index].tinable = value ? 1 : 0;

      const surface = setStore.triggerSurfaceDesignerVm?.surfaceDef;
      const pointId = setStore.xyzPointsTable[index].id;

      const pointProcessingType = await surface?.autoRegenerateBorder(pointId, value);
      if (pointProcessingType === "regular") {
        setStore.xyzPointsTable[index].editing_status = AppConstants.SURFACE_USER_EDIT;
        setStore.initialXyzPointsTable[index].editing_status = AppConstants.SURFACE_USER_EDIT;
      } else if (pointProcessingType === "border and modify") {
        setStore.triggerSurfaceDesignerVm.addBorderTool?.reDrawBorder();
        setStore.xyzPointsTable[index].editing_status = AppConstants.SURFACE_USER_EDIT;
        setStore.initialXyzPointsTable[index].editing_status = AppConstants.SURFACE_USER_EDIT;
      }

    } else if (name === 'visible') {
      setStore.xyzPointsTable[index].visible = value ? 1 : 0;
      if (!value) {
        setStore.xyzPointsTable[index].tinable = 0;

        const surface = setStore.triggerSurfaceDesignerVm?.surfaceDef;
        const pointId = setStore.xyzPointsTable[index].id;

        const pointProcessingType = await surface?.autoRegenerateBorder(pointId, value);
        if (pointProcessingType === "regular") {
          setStore.xyzPointsTable[index].editing_status = AppConstants.SURFACE_USER_EDIT;
          setStore.initialXyzPointsTable[index].editing_status = AppConstants.SURFACE_USER_EDIT;
        } else if (pointProcessingType === "border and modify") {
          setStore.triggerSurfaceDesignerVm.addBorderTool?.reDrawBorder();
          setStore.xyzPointsTable[index].editing_status = AppConstants.SURFACE_USER_EDIT;
          setStore.initialXyzPointsTable[index].editing_status = AppConstants.SURFACE_USER_EDIT;
        } else {
          setStore.xyzPointsTable[index].visible = 1;
        }
      }
      setStore.initialXyzPointsTable[index].visible = value ? 1 : 0;
    }
    if (!setStore.isImportingNewSurvey) {
      setStore.triggerSurfaceDesignerVm?.surfaceDef?.logPendingEdits();
      setStore.triggerSurfaceDesignerVm?.drawSurveyPoints();
      setStore.triggerBreaklineToolboxVM?.drawGroundPoints();
    }

    // DON'T UNCOMMENT THE FOLLOWING
    // if (setStore.xyzPointsTable[index].id !== undefined) {
    //   setStore.xyzPointsTable[index].editing_status = 'update';
    // }
  }

  @action.bound handleSelectAll(name, value: boolean) {
    // const target = event.target;
    // const value = target.type === 'checkbox' ? target.checked : target.value;
    // const name = target.name;

    setStore.xyzPointsTable.forEach((point, i) => {
      // DON'T UNCOMMENT THE FOLLOWING
      // if (setStore.xyzPointsTable[i]['id'] !== undefined && setStore.xyzPointsTable[i][name] !== value) {
      //   setStore.xyzPointsTable[i]['editing_status'] = 'update';
      // }
      if (point.filtered) {
        setStore.xyzPointsTable[i][name] = value ? 1 : 0;
        if (name === 'visible' && !value) {
          setStore.xyzPointsTable[i]['tinable'] = 0;
        }
        if (name === 'tinable' && value) {
          setStore.xyzPointsTable[i]['visible'] = 1;
        }
      }
    });

    if (!setStore.isImportingNewSurvey) {
      setStore.triggerSurfaceDesignerVm?.surfaceDef?.logPendingEdits();
      setStore.triggerSurfaceDesignerVm?.drawSurveyPoints();
      setStore.triggerBreaklineToolboxVM?.drawGroundPoints();
    }
  }

  //newly added row, input fields onChange functionality
  @action.bound handleOnChange(event: any) {
    const target = event.target;
    const value = target.value;
    const name = target.name;
    const index = target.getAttribute('data-i');
    setStore.xyzPointsTable[index][name] = value;
    setStore.initialXyzPointsTable[index][name] = value;
  }

  //input fields onChange functionality for Editing
  @action.bound handleOnChangeEdit(event: any) {
    if (setStore.pointDescriptionFilter !== "All") {
      this.setDescriptionFilter("All");
    }
    const target = event.target;
    const value = target.value;
    const name = target.name;
    const index = target.getAttribute('data-i');
    setStore.xyzPointsTable[index][name] = value;
    setStore.initialXyzPointsTable[index][name] = value;
    if (!setStore.isImportingNewSurvey) {
      setStore.triggerSurfaceDesignerVm?.surfaceDef?.logPendingEdits();
    }
    // DON'T UNCOMMENT THE FOLLOWING
    // if (setStore.xyzPointsTable[index]['id'] !== undefined) {
    //   setStore.xyzPointsTable[index]['editing_status'] = 'update';
    //   setStore.initialXyzPointsTable[index]['editing_status'] = 'update';
    // }
  }

  //delete button onClick functionality
  @action.bound onClickDelete() {
    this.checkboxes.forEach((e) => {
      setStore.xyzPointsTable[e].editing_status = 'delete';
      setStore.initialXyzPointsTable[e].editing_status = 'delete';
    });
    setStore.xyzPointsTable = setStore.xyzPointsTable.filter(
      (item) => item.id !== undefined || item.editing_status === 'add'
    );
    setStore.initialXyzPointsTable = setStore.initialXyzPointsTable.filter(
      (item) => item.id !== undefined || item.editing_status === 'add'
    );
    this.checkboxes = [];
    setStore.saveTINSurface = true;
  }

  //accept header onClick functionality
  @action.bound async onClickAcceptHeader() {
    if (!this.formValidation.formValid) {
      for (const field in this.newSurvey) {
        const value = this.newSurvey[field];
        await this.validateField(field, value);
      }
      if (!this.formValidation.formValid) {
        return;
      }
    }
     // Validate survey data bounds before proceeding
    if (!(await this.validateSurveyDataBounds())) {
      return false;
    }
    await this.getProjectionfromUploadSurvey(designStore.projectsStore.currentProject.state, designStore.projectsStore.currentProject.county)

    if (this.projectionIsNotMatch) {
      this.projectionIsNotMatch = false;
      return false;
    }
    if (this.hasDuplicates(setStore.surveyFileHeader)) {
      await MessageCenter.addNotificationFromCode('Survey_01'); // Same header is used for multiple columns in the survey.
      return false;
    }
    await this.setXYZPoints();
    setStore.isHeaderAccepted = true;
    this.previewProjectedSurvey = false;

    if (designStore.currentSurface.surfaceDefTitle !== appConfig.tempHardCodes.surfaceDefTitle) {
      await this.updateSurfaceName();
    }

    if (libraryStore.projectSpatialRefData.coordinateType !== this.newSurvey.coordinateSystemType || libraryStore.projectSpatialRefData.zone !== this.newSurvey.zone) {
      this.spatialRefPayload = this.createSpatialRef();     
    }

    this.displayGroundPointsHandler({target: {checked: true}});
    this.props.startWatchHandler();

    return true;
  }

  @action.bound createSpatialRef(): SpatialRefRequestDto | null {
    const zoneValue = (this.newSurvey.zone && this.newSurvey.zone !== '') ? this.newSurvey.zone : libraryStore.projectSpatialRefData.zone;
    let projectionName = libraryStore.pojectionByLocationData.length === 1
      ? libraryStore.pojectionByLocationData[0].projectionName
      : libraryStore.pojectionByLocationData.find((data) => data.zoneName === this.newSurvey.zone)?.projectionName || '';

    const shouldAddFtIntl = !projectionName.includes('_Ft_Intl');
    projectionName = shouldAddFtIntl ? `${projectionName}_Ft_Intl` : projectionName;

    projectionName = projectionName.includes("Ft_Intl") ? projectionName : `${projectionName}_Ft_Intl`;

    const postJSON: SpatialRefRequestDto = {
      coordinateType: this.newSurvey.coordinateSystemType,
      createdBy: String(localStorage['username']),
      espg: libraryStore.pojectionByLocationData[0]?.epsgCode,
      projectUuid: designStore.projectsStore.currentProjectId,
      projectionName: (this.newSurvey.zone && this.newSurvey.zone !== '') || (this.newSurvey.coordinateSystemType !== libraryStore.projectSpatialRefData.coordinateType) ? projectionName : libraryStore.projectSpatialRefData.projectionName,
      unit: 'Feet',
      wkid: libraryStore.pojectionByLocationData[0]?.esriWkid,
      zone: zoneValue,
    };

    // Check if any of the payload properties are undefined or empty
    if (Object.values(postJSON).some(value => value === undefined || value === '')) {
      return null; // Stop the function execution
    }

    return postJSON
  }

  //cancel edit button onClick functionality
  @action.bound onClickCancelXYZSurvey = () => {
    setStore.currentSurvey.surveyName = '';
    setStore.currentSurvey.surveyId = '';
    this.setDescriptionFilter("All");
    setStore.surveyEditMode = false;
    libraryStore.pojectionByLocationData = []
    this.newSurvey.coordinateSystemType = libraryStore.projectSpatialRefData.coordinateType ? libraryStore.projectSpatialRefData.coordinateType : '';
    this.newSurvey.zone = libraryStore.projectSpatialRefData.zone ? libraryStore.projectSpatialRefData.zone : ''
    cancelEditXYZSurvey();
    this.removeUtilityLayers();
  };

  //add button onClick functionality
  @action.bound onClickAdd() {
    const newPoint = {
      pointName: '',
      x: 0.0,
      y: 0.0,
      z: 0.0,
      featureDescription: '',
      visible: 1,
      tinable: 1,
      editing_status: 'add',
    };
    setStore.xyzPointsTable.push(newPoint);
    setStore.initialXyzPointsTable.push(newPoint);
    setStore.saveTINSurface = true;
  }

  //set the data in xyzPointsTable
  @observable duplicatesList: Array<any> = [];

  @action.bound async setXYZPoints() {
    const header = toJS(setStore.surveyFileHeader);
    if (setStore.hasFileHeader) {
      setStore.tempXYZPointsTable.shift();
    }
    setStore.xyzPointsTable = null;
    setStore.xyzPointsTable = [];
    setStore.tempXYZPointsTable.map((item, i) => this.createSurveyPoint(header, item, i));

    if (this.duplicatesList.length > 0) {
      const sortedArray = setStore.xyzPointsTable.sort((a, b) => b.hasDuplicate - a.hasDuplicate);
      setStore.xyzPointsTable = sortedArray;
      await MessageCenter.addNotificationFromCode('Survey_13'); // 'Uploaded data has duplicates.',
    }

    // create the same initialTable
    setStore.initialXyzPointsTable = setStore.xyzPointsTable.map((item) => {
      return { ...item };
    });
    setStore.initXYZPointsLength = setStore.xyzPointsTable.length;
    setStore.maxId = setStore.xyzPointsTable.length;
  }

  //creating a survey point object with selected header and value
  @action.bound createSurveyPoint(header, value, index) {
    const surveyPoints: any = {};
    const numericHeaders = ['x', 'y', 'z'];
    for (let i = 0; i < header.length; i++) {
      if (numericHeaders.includes(header[i])) {
        surveyPoints[header[i]] = Number(value[i]);
      } else {
        surveyPoints[header[i]] = value[i];
      }
    }
    if (!surveyPoints.pointName) {
      surveyPoints.pointName = 'n/a';
    }
    if (!surveyPoints.featureDescription) {
      surveyPoints.featureDescription = 'n/a';
    }

    surveyPoints.id = index;
    surveyPoints.editing_status = AppConstants.SURFACE_ORIGINAL;

    // checking for duplicates with savedSurveyArray
    const duplicates: any[] = [];

    for (let i = 0; i < setStore.allGroundPoints?.length; i++) {
      if (setStore.allGroundPoints[i].x === surveyPoints.x && setStore.allGroundPoints[i].y === surveyPoints.y) {
        duplicates.push(setStore.allGroundPoints[i]);
      }
    }

    if (duplicates.length > 0) {
      surveyPoints.visible = 0;
      surveyPoints.tinable = 0;
      surveyPoints.hasDuplicate = 1;
      duplicates.forEach((d) => {
        const existingIndex = setStore.xyzPointsTable.findIndex(
          (el) => el.x === d.x && el.y === d.y
        );
        if (existingIndex >= 0) {
          setStore.xyzPointsTable[existingIndex].hasDuplicate = 1;
        }
      });
      this.duplicatesList.push(surveyPoints);
    }

    else {

      // checking for duplicates with existing xyzPointsTable array
      const indexOfDuplicate = setStore.xyzPointsTable.findIndex(
        (el) => surveyPoints.x === el.x && surveyPoints.y === el.y
      );

      if (indexOfDuplicate === -1) {
        surveyPoints.visible = 1;
        surveyPoints.tinable = 1;
        surveyPoints.hasDuplicate = 0;
      } else {
        surveyPoints.visible = 0;
        surveyPoints.tinable = 0;
        surveyPoints.hasDuplicate = 1;
        setStore.xyzPointsTable[indexOfDuplicate].hasDuplicate = 1;
        this.duplicatesList.push(surveyPoints);
      }
    }
    setStore.xyzPointsTable.push(surveyPoints);
  }

  //validate if headers are unique
  hasDuplicates(array) {
    return new Set(array).size !== array.length;
  }

  @action.bound removeUtilityLayers() {
    setStore.selectedSurveysInfo.map((lyrId) => removeSingleLayerFromMap(lyrId));
    setStore.selectedSurveysInfo = [];

    if (gisModel.map) {
      const transSketchLayer = gisModel.map.findLayerById(AppConstants.transSketchLayerId);
      if (transSketchLayer) {
        gisModel.map.remove(transSketchLayer);
      }
      const rotateSketchLayer = gisModel.map.findLayerById(AppConstants.rotateSketchLayerId);
      if (rotateSketchLayer) {
        gisModel.map.remove(rotateSketchLayer);
      }

      // remove green triangles layer
      if (!setStore.currentSurvey.surveyId) removeSingleLayerFromMap('new_survey');

      setStore.pointsOnMap = false;
    }
  }

  //save button onClick functionality
  @action.bound async handleSaveXYZSurvey() {
    stores.AppStore.designStore.setLoader('Saving survey...');
    // Call the API with the stored payload before saving the survey
    if (this.spatialRefPayload) {
      try {
        await SpatialRefController.createSpatialRefUsingPOST(this.spatialRefPayload);

        this.spatialRefPayload = null;
      } catch (error) {
        MessageCenter.addError((error as Error).message);
      }
    }
    getSpatialRefByProject(designStore.projectsStore.currentProjectId)
    const response = await saveXYZSurvey();
    if (gisModel.view) {
      // update the surface elevation layer
      try {
        await DesignContainerVM.loadOriginalSurface();
        // await DesignContainerVM.updateSurfaceTinElevationLayer();
        // await DesignContainerVM.instance.openOrGenerateSurface(true);

        if (setStore.triggerSurfaceDesignerVm?.surfaceDef) setStore.triggerSurfaceDesignerVm.surfaceDef = undefined;
        if (setStore.triggerSurfaceDesignerVm?.surfaceDef) console.log('SurfaceDef is triggered.');
        setStore.isImportingNewSurvey = false;
        this.removeUtilityLayers();
        await this.switchToEditMode(response);
        this.outsidePoints(response);
        // setStore.resetTabs();
      } catch (error) {
        MessageCenter.addError((error as Error).message);
      }
    }
    // setStore.resetXYZSurvey();
    this.setUniqueDescriptions();
    stores.AppStore.designStore.setLoader('');
  }

  async switchToEditMode(response) {
    try {
      await getSurveys();
      setStore.surveyEditMode = true;
      if (response && response.data) setStore.currentSurvey.surveyId = response.data.surveyId;
      setStore.currentSurvey = setStore.surveySummaryTable[0];
      if (setStore.currentSurvey.surveyType === appConfig.hardCodes.surveyTypeXYZ) {
        setStore.currentTab = 'Edit Survey';
        setStore.maxId = 0;
        setStore.initXYZPointsLength = 0;
        setStore.hasSurvey = true;
        setStore.hasGrid = true;

        await SurfaceDef.fetchSurfaceDefWithBreaklinesAndUpdatedBorder(designStore.projectsStore.currentProject.sessionList[0].sessionUuid);
 
        const surveyEditable = setStore.surveyEditables.find((s) => s.surveyId === setStore.currentSurvey.surveyId);
        if (surveyEditable && surveyEditable.groundPoints) {
          setStore.xyzPointsTable = surveyEditable.groundPoints;
          setStore.initialXyzPointsTable = surveyEditable.groundPoints;
        }        
        setStore.initXYZPointsLength = setStore.xyzPointsTable.length;
        setStore.maxId = setStore.xyzPointsTable.length;

        setStore.triggerSurfaceDesignerVm?.drawSurveyPoints();
        setStore.triggerBreaklineToolboxVM?.drawGroundPoints();

        this.props.startWatchHandler();
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }


  @action.bound outsidePoints(response) {
    if (response.data && response.data.outsidePoints) {
      const responseOutsidePoints = response.data.outsidePoints;
      responseOutsidePoints.forEach((outsidePoint) => {
        const index = setStore.xyzPointsTable.findIndex((point) => outsidePoint.pointName === point.pointName.toString());
        if (index !== -1) {
          setStore.xyzPointsTable[index].tinable = 0;
          let id = `key-xyzSurveyPoint-${index}`;
          const element = document.getElementById(id);
          if (element) {
            element.classList.add("highlightedRow");
          };
        }
      });
      if (response.data.errorResponse && response.data.errorResponse.errorMessage && response.data.errorResponse.errorCode) {
        MessageCenter.addWarning(response.data.errorResponse.errorMessage, undefined, response.data.errorResponse.errorCode);
      }
    }
  }

  @action.bound noProjecMessage(history) {
    if (!designStore.hasProject()) {
      MessageCenter.addWarning(designStore.noProjectMessage, [
        {
          callback: () => {
            history?.push('/projects/new-project');
            MessageCenter.clearMessages();
          },
          text: 'Go Create a Project',
        },
      ]);
    }
  }

  @action.bound displayGroundPointsHandler(e: any) {
    setStore.displayNewSurvey = e.target.checked;
    this.onDisplayMap(e.target.checked);
  }
}